import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { canAccessFeature } from "../../auth/featureScopes";
import { AccountCreateEditModal } from "../../components/accounts/AccountCreateEditModal";
import HauliersTable from "../../components/hauliers/HauliersTable";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { LinkButton } from "../../components/shared/Inputs/LinkButton";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useHaulierAccounts from "../../data/useHaulierAccounts";
import { IAccount } from "../../models/account/IAccount";

// Hauliers are ordinary accounts flagged "Waste carrier (haulier)". This page is the filtered
// view of them reached from Waste Tracking. Anyone who can use Waste Tracking can add or edit a
// haulier here; the full account page (with the Waste Movements history) needs the Accounts scope.
export default function Hauliers() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const { featureScopes } = useAuth();
  const canOpenAccount = canAccessFeature(featureScopes, "accounts");
  const { haulierAccounts } = useHaulierAccounts("Hauliers");
  const [searchTerm, setSearchTerm] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<IAccount | undefined>(undefined);

  useEffect(() => {
    setInfo({
      name: "Hauliers",
      desc: "Accounts flagged as waste carriers, for picking on the Waste Tracking page",
    });
  }, []);

  const visible = useMemo(() => {
    const term = searchTerm.trim().toLowerCase().replace(/\s/g, "");
    const all = haulierAccounts.data?.data ?? [];
    if (!term) return all;
    return all.filter(
      (a) =>
        a.name.toLowerCase().replace(/\s/g, "").includes(term) ||
        a.carrierRegistrationNumber?.toLowerCase().replace(/\s/g, "").includes(term) ||
        a.haulierVehicleRegistration?.toLowerCase().replace(/\s/g, "").includes(term) ||
        String(a.accountNumber).includes(term)
    );
  }, [haulierAccounts.data, searchTerm]);

  const openHaulier = (account: IAccount) => {
    if (canOpenAccount) {
      navigate(`/accounts/${account.id}`);
      return;
    }
    setEditing(account);
    setEditOpen(true);
  };

  return (
    <>
      <AccountCreateEditModal
        open={addOpen}
        setOpen={setAddOpen}
        presetHaulier
        onCreated={(account: IAccount) => {
          if (canOpenAccount) navigate(`/accounts/${account.id}`);
        }}
      />
      <AccountCreateEditModal open={editOpen} setOpen={setEditOpen} account={editing} />

      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div className="relative flex flex-grow items-stretch focus-within:z-10">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 sm:text-sm"
            placeholder="Name, account number or registration"
          />
        </div>
        <div className="flex items-center gap-2">
          <LinkButton label="Back to Waste Tracking" to="/waste-tracking" classes="bg-gray-500 hover:bg-gray-600" />
          <ButtonInput label="Add haulier" isSubmit={false} onClick={() => setAddOpen(true)} classes={undefined} />
        </div>
      </div>

      <p className="mt-2 text-sm text-gray-500">
        {canOpenAccount
          ? "Open a haulier to edit its details, contacts and addresses, or to see the waste movements it has delivered."
          : "Open a haulier to edit its details, contacts and addresses. The full account page, with its waste movement history, needs the Accounts permission."}
      </p>

      {haulierAccounts.isLoading ? (
        <div className="my-4 mt-8 min-h-[50vh] rounded-lg bg-white p-4 shadow">
          <LoadingWheel />
        </div>
      ) : (
        <HauliersTable accounts={visible} onOpen={openHaulier} />
      )}
    </>
  );
}
